import { z } from 'zod';

/**
 * Что показывает экран замера — целиком в адресе (`rules/lists-and-filters.mdc` §1).
 *
 * Списка прогонов контракт не публикует: идентификатор печатает `scripts/run-evaluation.ts` по
 * окончании прогона, и приносит его человек. Поэтому адрес здесь не удобство, а единственный
 * способ вернуться к тому же результату завтра и переслать его вместе с выводом — «модель B лучше
 * модели A» без ссылки на два прогона есть мнение, а не замер.
 *
 * **Каждое поле откатывается `.catch(undefined)`, а не роняет разбор.** Упавший `validateSearch`
 * заменяет экран границей ошибки, а адрес с мусором остаётся в строке — перезагрузка не помогает.
 * Мусорный параметр обязан значить «не выбрано», а не «сломано».
 *
 * Проверка формы делается здесь, а не сервером: `runId` объявлен `format: uuid`, и «последний»
 * получил бы `422` — запрос, посланный заведомо зря.
 */

/**
 * Образец идентификатора набора — тот же, что в `components/parameters/GoldSetId`.
 *
 * Набор — конфигурация установки: идентификатор пишет автор файла разметки, он не UUID и не
 * идентификатор прогона. Повторён здесь потому, что `openapi-typescript` не переносит `pattern` в
 * типы: без него в путь маршрута ушла бы любая строка, включая `../`.
 */
const GOLD_SET_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/u;

/**
 * Идентификатор прогона — ровно то, что принимает маршрут.
 *
 * Вынесен отдельным значением, потому что у него два потребителя: разбор адреса (ниже) и поле
 * формы (`evaluation-picker-form.schema.ts`). Две записи одного условия разошлись бы молча и
 * привели бы к худшему из состояний — форма приняла бы значение, которое разбор адреса тут же
 * отбросил бы, и экран показал бы «ничего не выбрано» сразу после нажатия «Показать».
 */
export const evaluationRunIdSchema = z.uuid();

/** Идентификатор набора — тот же образец, и по той же причине вынесен. */
export const evaluationGoldSetIdSchema = z.string().regex(GOLD_SET_ID);

export const evaluationSearchSchema = z.object({
  /** Прогон, чья сводка открыта. Он же левая сторона сравнения. */
  run: evaluationRunIdSchema.optional().catch(undefined),
  /** Второй прогон сравнения. Осмыслен только вместе с первым, но проверяется отдельно от него. */
  against: evaluationRunIdSchema.optional().catch(undefined),
  /**
   * Набор, чьи адреса проверяются.
   *
   * Необязателен: по умолчанию берётся набор открытого прогона — сервер уже назвал его в
   * происхождении результата, и спрашивать это второй раз значило бы спрашивать известное.
   */
  goldSet: evaluationGoldSetIdSchema.optional().catch(undefined),
});

export type EvaluationSearch = z.infer<typeof evaluationSearchSchema>;
